import React, { TouchEvent as ReactTouchEvent, useCallback, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { When } from 'react-if'
import LazyHydrate from 'react-lazy-hydration'
import { useMediaQuery } from 'react-responsive'
import { Button } from '@/atoms/Button'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { ExternalLinkIcon } from '@/atoms/Icons/ExternalLinkIcon'
import { AsP, Footer, HeadingXS, ParagraphSM } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { BeforeYouLeaveProvider } from '@/contexts/BeforeYouLeaveContext'
import { BeforeYouLeaveModal } from '@/molecules/BeforeYouLeaveModal/BeforeYouLeaveModal'
import { EmbeddedVideo } from '@/molecules/EmbeddedVideo'
import { AngelFundingLink } from '@/molecules/InvestLink'
import { Modal } from '@/molecules/Modal'
import { SimpleThumbnail } from '@/molecules/Thumbnail'
import { useBotDetection } from '@/services/BotDetection'
import { useGuildMember } from '@/services/GuildUserService'
import { useUserRegion } from '@/services/UserRegionService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { isGuildFullySupported } from '@/utils/GuildUtil'
import { useSafeTrack } from '@/utils/analytics'
import { getBooleanFromLocalStorage, writeToLocalStorage } from '@/utils/local-storage'
import { Translate, TranslateFunction, useTranslate } from '@/utils/translate/translate-client'

export const AnnouncementModal: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const [touchStart, setTouchStart] = useState(0)
  const [touchEnd, setTouchEnd] = useState(0)
  const minSwipeDistance = 250
  const ref = useRef<HTMLDivElement | null>(null)
  const { isGuildMember, loading } = useGuildMember()
  const { t } = useTranslate('common')
  const { region } = useUserRegion()
  const { isBot } = useBotDetection()

  const isGuildSupported = isGuildFullySupported(region)
  const shouldRender = isGuildSupported && !isBot
  const isDesktopOrLaptop = useMediaQuery({ query: '(min-width: 768px)' })

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false)
    writeToLocalStorage('hasSeenInvestmentModal', true)
  }, [])

  useEffect(() => {
    // Check if current date is after the cutoff date (September 1, 2025)
    const currentDate = new Date()
    const cutoffDate = new Date('2025-09-01T23:59:59')

    // Don't show after the 1st of September, 2025
    if (currentDate > cutoffDate) {
      return
    }

    const hasSeenModal = getBooleanFromLocalStorage('hasSeenInvestmentModal')

    if (!hasSeenModal) {
      if (!loading) {
        setIsModalOpen(true)
      }
    }
  }, [isGuildMember, loading])

  const onTouchStart = useCallback((event: ReactTouchEvent) => {
    setTouchEnd(0)
    setTouchStart(event.targetTouches[0]?.clientY || 0)
  }, [])

  const onTouchMove = useCallback((event: ReactTouchEvent) => setTouchEnd(event.targetTouches[0]?.clientY || 0), [])

  const onTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd) return
    const html = window.document.getElementsByTagName('html')[0]
    if (!html) return
    html.style.overflow = 'hidden'
    const distance = touchStart - touchEnd
    const isSwipeDown = distance < -minSwipeDistance
    if (isModalOpen && isSwipeDown) {
      handleModalClose()
      html.style.overflow = 'auto'
    }
  }, [handleModalClose, isModalOpen, touchStart, touchEnd])

  const handleClickOutside = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (!isModalOpen) return
      const targetNode = event.target as Node | null
      if (!targetNode) return
      if (ref.current?.contains(targetNode)) return // Click inside the modal panel
      // Detect Osano (or similar) consent banner clicks: walk up ancestors looking for a class containing 'osano-cm'.
      let current: Node | null = targetNode
      while (current && current !== document.body) {
        if (current instanceof HTMLElement) {
          for (const cls of current.classList) {
            if (cls.includes('osano-cm')) return // Inside cookie consent UI; do not close
          }
        }
        current = current.parentNode
      }
      handleModalClose() // Genuine outside click
    },
    [ref, handleModalClose, isModalOpen],
  )

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('touchstart', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
    }
  }, [handleClickOutside])

  useEffect(() => {
    if (isModalOpen) {
      setIsVisible(true)
    } else {
      setIsVisible(false)
    }
  }, [isModalOpen])

  if (!shouldRender) return null

  return (
    <LazyHydrate whenVisible>
      <>
        <When condition={isDesktopOrLaptop}>
          <Modal
            isOpen={isModalOpen}
            onClose={handleModalClose}
            panelClasses="md:min-w-[88vw] lg:min-w-min !p-4 !pb-8 !max-w-[834px]"
            className=""
          >
            <AnnouncementModalBody
              isGuildMember={isGuildMember}
              t={t}
              handleModalClose={handleModalClose}
              loading={loading}
            />
          </Modal>
        </When>
        <When condition={!isDesktopOrLaptop}>
          {isVisible && (
            <div
              className={classNames(
                'fixed inset-0 z-[1999]  backdrop-opacity-70 transition-all',
                isVisible ? 'bg-core-gray-950/70' : 'bg-transparent',
              )}
            />
          )}
          <div
            ref={ref}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            className={classNames(
              'box-border fixed bottom-0 left-0 z-[2000] max-h-[90vh] w-full rounded-t-3xl border-t bg-core-gray-100 transition-all duration-300 ease-in-out',
              'sm:max-w-[400px] sm:right-0 sm:mx-auto sm:rounded-b-3xl sm:overflow-hidden sm:h-fit',
              isVisible
                ? 'translate-y-0 sm:my-auto sm:bottom-0 sm:top-0 sm:opacity-1'
                : 'translate-y-full sm:opacity-0',
            )}
          >
            <div className="p-4 pb-8">
              <AnnouncementModalBody
                isGuildMember={isGuildMember}
                t={t}
                handleModalClose={handleModalClose}
                loading={loading}
              />
            </div>
          </div>
        </When>
      </>
    </LazyHydrate>
  )
}

interface AnnouncementModalBodyProps {
  isGuildMember: boolean
  loading: boolean
  t: TranslateFunction
  handleModalClose: () => void
}

const AnnouncementModalBody: React.FC<AnnouncementModalBodyProps> = ({
  isGuildMember,
  loading,
  t,
  handleModalClose,
}) => {
  const track = useSafeTrack()
  const [loadPlayer, setLoadPlayer] = useState(false)
  const leavingMessage = t('youAreLeavingToRegAAngel', 'You are now leaving Angel, Inc. to view a Reg A+ offering')
  const leavingContinueLabel = t('continueToOffering', 'Continue to Offering')

  const handleClick = useCallback(() => {
    track('Announcement Modal CTA Clicked', isGuildMember)
  }, [isGuildMember, track])

  const handleLoadPlayer = useCallback(() => {
    setLoadPlayer((prev) => !prev)
  }, [])

  return (
    <BeforeYouLeaveProvider>
      <BeforeYouLeaveModal />
      <div className="flex flex-col text-black">
        <div className="flex items-center justify-end">
          <div onClick={handleModalClose} className="cursor-pointer">
            <CloseIcon color="black" size={24} className="cursor-pointer" />
          </div>
        </div>
        <div className="flex flex-col gap-y-4 md:gap-y-6 md:px-6">
          <HeadingXS weight="bold" className="md:photon-heading-sm xl:photon-heading-md text-pretty">
            {t('buildTheFutureOfEntertainmentWithUs', 'Build the Future of Entertainment with Us!')}
          </HeadingXS>
          <When condition={!loadPlayer}>
            <SimpleThumbnail
              imageUrl={getCloudinaryImageUrl({
                path: '/v1755282522/angel-studios/landing/angel_invest.webp',
                height: 425,
                width: 754,
              })}
              showButton
              onClick={handleLoadPlayer}
              className={'rounded-2xl'}
            />
          </When>
          <When condition={loadPlayer}>
            <div className="overflow-hidden rounded-2xl">
              <EmbeddedVideo
                className="w-full"
                title={t('investAngelRegA', 'Invest in Angel Regulation A+ Q2 2025')}
                url={`https://www.youtube.com/embed/Cp-GPE6ac08?autoplay=1&si=vLI6gX7Ca_7JvIX7`}
              />
            </div>
          </When>
          <div className="flex flex-col items-center gap-y-2 text-center md:gap-y-6">
            <ParagraphSM color="core-gray-700" className={classNames('max-w-[670px]')}>
              <Translate t={t} i18nKey="supportAngelMission">
                Support Angel’s mission to amplify light and share in its success.
              </Translate>
            </ParagraphSM>
            <Footer color="core-gray-700" as={AsP} className="relative text-pretty">
              <Translate i18nKey="angel3DisclaimerUpdated" t={t}>
                Angel Studios, Inc. is conducting an offering of securities under Regulation A. Investing in this
                offering is high-risk and Angel Studios, Inc. does NOT guarantee performance of the investment. You
                should not invest unless you can afford to lose the full amount invested. A description of the terms of
                the offering and your investment, can be found in the subscription agreement available at{' '}
                <a target="_blank" href="https://investinangel.com">
                  investinangel.com
                </a>
                . You should not invest until you have reviewed and understood the offering documents, including the
                risk factors contained in the offering circular.
              </Translate>
            </Footer>
            <div className="mx-auto w-fit">
              {!loading && (
                <>
                  <AngelFundingLink
                    href={paths.external.investmentFund.angel3}
                    linkContext="Announcement Modal"
                    leavingMessage={leavingMessage}
                    leavingContinueLabel={leavingContinueLabel}
                    className="w-auto"
                  >
                    <Button size="lg" variant="oxide" onClick={handleClick}>
                      {t('investNow', 'Invest Now')}
                      <ExternalLinkIcon size={28} color="white" className="ml-1.5" />
                    </Button>
                  </AngelFundingLink>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </BeforeYouLeaveProvider>
  )
}
